/* eslint-disable @typescript-eslint/no-explicit-any -- any используется как значение параметров инвариантных типов */

import { Dictionary, DistributiveOmit, IsNever, KeysOf } from '@direct-frontend/stdlib';
import { getDefaultValue, DefaultValueOrFactory } from '../utils/getDefaultValue';
import { NeverToEmpty, ObjectField, ObjectFieldInstance } from './createObjectField';
import { Primitive, PrimitiveField } from './createPrimitiveField';
import {
    BaseFields,
    BoundFieldActions,
    FieldActionParams,
    InferFieldValue,
    InferFieldsValue,
    SetValueOptions,
} from './types';
import { CalculatedFieldStateProperties, ObjectFieldState } from '../store/types';

/** Объект с контекстом для синхронного экшена union поля (расширяет FieldActionParams) */
export type UnionFieldActionParams<
    Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
> = FieldActionParams<
    InferFieldsValue<UnionConstituents[number]['fields']>,
    ObjectFieldState<InferFieldsValue<UnionConstituents[number]['fields']>, never>,
    never
> & {
    getValueByKey: <KeyValue extends InferFieldValue<UnionConstituents[number]['fields'][Key]>>(
        unionKey: KeyValue
    ) => InferValueByKeyValue<Key, KeyValue, UnionConstituents[number]> | undefined;
    getStateByKey: <KeyValue extends InferFieldValue<UnionConstituents[number]['fields'][Key]>>(
        unionKey: KeyValue
    ) => InferUnionFieldConstituentFieldState<Key, KeyValue, UnionConstituents[number]> | undefined;
};

/** Тип экшена union поля. Принимает контекст с draftValue, getContext, getState, getValueByKey, getStateByKey и произвольные аргументы */
export type UnionFieldAction<
    Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
    Args extends unknown[] = [],
> = (
    context: UnionFieldActionParams<Key, UnionConstituents>,
    ...args: Args
) => void | undefined | InferFieldsValue<UnionConstituents[number]['fields']>;

/** Объект со всеми экшенами в декларации union поля */
export type UnionFieldActionsMap<
    Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
    Args extends unknown[] = any[],
> = Dictionary<UnionFieldAction<Key, UnionConstituents, Args>>;

/** Отбрасывает экшены union поля, если они не переданы и TypeScript не вывел их тип */
export type SkipUnknownUnionFieldActions<
    Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
    UnionFieldActions,
> = UnionFieldActionsMap<Key, UnionConstituents, any[]> extends UnionFieldActions ? never : UnionFieldActions;

/** Базовый тип для любого поля-юниона */
export type BaseUnionField = UnionField<any, any, any>;
/** Базовый тип для инстанса любого поля-юниона */
export type BaseUnionFieldInstance = UnionFieldInstance<any, any, any, any>;
/** Базовый тип для элемента поля-юниона с заданным ключом */
export type BaseUnionConstituent<Key extends string> = ObjectField<
    { [K in Key]: PrimitiveField<Primitive, any, never, any, any, any, any> },
    any,
    never,
    any,
    any,
    any,
    any
>;

/** Поле-юнион – позволяет работать со значением одного из объектных полей */
export interface UnionField<
    Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
    FieldActions extends UnionFieldActionsMap<Key, UnionConstituents>,
> {
    type: 'union';
    key: Key;
    constituents: UnionConstituents;
    defaultValue: InferFieldsValue<UnionConstituents[number]['fields']>;
    actions?: FieldActions;
}

export type InferKeyValue<Key extends string, InnerFields extends BaseFields> = InferFieldValue<InnerFields[Key]>;

/**
 * Generic тип, принимающий название поля ключа, одно из значений этого поля и возможные FieldValue
 *
 * Возвращает тип поля, у которого будет данное значение ключа
 */
export type InferValueByKeyValue<
    Key extends string,
    KeyValue extends InferFieldValue<UnionConstituent['fields'][Key]>,
    UnionConstituent extends BaseUnionConstituent<Key>,
> = Extract<InferFieldsValue<UnionConstituent['fields']>, { [K in Key]: KeyValue }>;

export type InferUnionFieldConstituentFieldState<
    Key extends string,
    KeyValue extends InferFieldValue<UnionConstituent['fields'][Key]>,
    UnionConstituent extends BaseUnionConstituent<Key>,
> = DistributiveOmit<
    ObjectFieldState<
        InferValueByKeyValue<Key, KeyValue, UnionConstituent>,
        UnionConstituent extends ObjectField<
            infer _,
            infer FieldValidationError,
            infer __,
            infer ___,
            infer ____,
            infer _____,
            infer ______
        >
            ? FieldValidationError
            : never
    >,
    CalculatedFieldStateProperties
>;

/** Базовый тип для любого объекта экшенов union поля */
export type BaseUnionFieldActionsMap = Dictionary<(...args: any[]) => any>;

/** Инстанс поля-юниона – предоставляет методы для работы с инстансом текущего объектного поля */
export type UnionFieldInstance<
    Key extends string,
    UnionConstituent extends BaseUnionConstituent<Key>,
    UnionFieldActions extends BaseUnionFieldActionsMap,
    FieldName extends string = string,
> = (UnionConstituent extends ObjectField<
    infer InnerFields,
    infer FieldValidationError,
    infer __,
    infer FieldContext,
    infer ObjectFieldActions,
    infer FieldSelectors,
    infer FieldAsyncActions
>
    ? Omit<
          ObjectFieldInstance<
              InnerFields,
              FieldValidationError,
              FieldContext,
              ObjectFieldActions,
              FieldSelectors,
              FieldAsyncActions,
              FieldName
          >,
          // Избавляемся от setValue, так как добавим его ниже с корректным типом
          'setValue' | 'actions'
      > & {
          /** Текущее значения поля-ключа, которое может быть использовано для сужения типа инстанса поля */
          key: InferKeyValue<Key, InnerFields>;

          /** Действия union-поля */
          actions: BoundFieldActions<
              // убираем never, если есть общие поля, то возвращаем ошибку
              NeverToEmpty<ObjectFieldActions> & NeverToEmpty<UnionFieldActions>
          >;
      }
    : never) & {
    /**
     * Обновляет текущее значение поля
     * Позволяет переключиться на другое объектное поле
     * @param value Новое значение поля
     * @param options Параметры для внутреннего использования
     **/
    setValue: (param: InferFieldsValue<UnionConstituent['fields']>, options?: SetValueOptions) => void;

    /**
     * Возвращает последнее значение поля для заданного ключа
     *
     * Можно использовать для логики по синхронизации значений разных key, или для восстановления
     * @param unionKey Значение поля-ключа
     */
    getValueByKey: <KeyValue extends InferFieldValue<UnionConstituent['fields'][Key]>>(
        unionKey: KeyValue
    ) => InferValueByKeyValue<Key, KeyValue, UnionConstituent> | undefined;

    /**
     * Возвращает fieldState для constituent'а заданного ключа
     *
     * Используем для логики по синхронизации значений разных key, или для восстановления
     * @param unionKey Значение поля-ключа
     */
    getStateByKey: <KeyValue extends InferFieldValue<UnionConstituent['fields'][Key]>>(
        unionKey: KeyValue
    ) => InferUnionFieldConstituentFieldState<Key, KeyValue, UnionConstituent> | undefined;
};

/**
 * Валидирует экшены union-поля на предмет конфликтов имен с экшенеми constituent полей.
 */
type ValidateUnionFieldActions<
    Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
    UnionFieldActions extends UnionFieldActionsMap<Key, UnionConstituents>,
> = IsNever<
    Extract<KeysOf<UnionFieldActions>, KeysOf<UnionConstituents[number]['actions']>>,
    UnionFieldActions,
    // eslint-disable-next-line @adv-frontend/config/no-rus-string-locales-rule --- текст ошибки
    'Найдены экшены с одинаковыми названиями в union-поле и его составных полях'
>;

/** Параметры для создания поля-юниона */
type UnionFieldOptions<
    Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
    UnionFieldActions extends UnionFieldActionsMap<Key, UnionConstituents>,
> = {
    /** Название поля-ключа, которое будет использоваться для разделения типов объектных полей */
    key: Key;
    /** Массив объектных полей, которые должны являться элементами юниона */
    fields: UnionConstituents;
    /** Значение поля по умолчанию */
    defaultValue: DefaultValueOrFactory<InferFieldsValue<UnionConstituents[number]['fields']>>;
    /** Экшены union-поля */
    actions?: ValidateUnionFieldActions<Key, UnionConstituents, UnionFieldActions>;
};

/** Создает поле-юнион */
export function createUnionField<
    const Key extends string,
    UnionConstituents extends BaseUnionConstituent<Key>[],
    UnionFieldActions extends UnionFieldActionsMap<Key, UnionConstituents>,
>({
    key,
    fields,
    defaultValue,
    actions,
}: UnionFieldOptions<Key, UnionConstituents, UnionFieldActions>): UnionField<
    Key,
    UnionConstituents,
    SkipUnknownUnionFieldActions<Key, UnionConstituents, NoInfer<UnionFieldActions>>
> {
    // Приводим тип к корректному, так как в возвращаемом значении параметры уточняются (удаляются any и unknown)
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return {
        type: 'union',
        key,
        constituents: fields,
        defaultValue: getDefaultValue(defaultValue),
        actions,
    } as UnionField<
        Key,
        UnionConstituents,
        SkipUnknownUnionFieldActions<Key, UnionConstituents, NoInfer<UnionFieldActions>>
    >;
}
